import React, { useEffect, useState } from "react";
import axios from "axios";

const formatIQD = (n) => {
    if (n === null || n === undefined) return "-";
    return Number(n).toLocaleString("en-US");
};

const formatUSD = (n) => {
    if (n === null || n === undefined) return "-";
    return `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

export default function LiveGoldRatePage() {
    const [data, setData] = useState(null);
    const [directionMap, setDirectionMap] = useState({});
    const [now, setNow] = useState(new Date());
    const [error, setError] = useState("");

    // live clock
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // fetch live gold
    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            try {
                const res = await axios.get("/api/live-gold");
                if (!mounted) return;
                setData((prev) => {
                    if (prev) {
                        const newDir = {};

                        const keys = ["k21", "k22", "k18"];

                        keys.forEach((k) => {
                            const oldVal = prev?.gold?.[k]?.iqdPerGram;
                            const newVal = res.data?.gold?.[k]?.iqdPerGram;

                            if (oldVal != null && newVal != null) {
                                if (newVal > oldVal) newDir[k] = "up";
                                else if (newVal < oldVal) newDir[k] = "down";
                                else {
                                    newDir[k] = "same";
                                }
                            }
                        });

                        setDirectionMap(newDir);
                    }

                    return res.data;
                }); setError("");
            } catch (e) {
                if (!mounted) return;
                setError("Failed to load live gold data.");
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 3000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    const rows = [
        {
            label: "21K Gold",
            sub: "Per 5 Grams (مثقال)",
            iqd: data?.gold?.k21?.iqdPerGram * 5,
            usd: data?.gold?.k21?.usdPerGram * 5,
            highlight: true,
        },
        {
            label: "22K Gold",
            sub: "Per 5 Grams (مثقال)",
            iqd: data?.gold?.k22?.iqdPerGram * 5,
            usd: data?.gold?.k22?.usdPerGram * 5,
        },
        {
            label: "18K Gold",
            sub: "Per 5 Grams (مثقال)",
            iqd: data?.gold?.k18?.iqdPerGram * 5,
            usd: data?.gold?.k18?.usdPerGram * 5,
        },

        {
            label: "Troy Ounce (XAU)",
            sub: "spot",
            iqd: data?.troyOunce?.iqd,
            usd: data?.troyOunce?.usd,
        },
        {
            label: "USD / IQD Rate",
            sub: "exchange rate",
            iqd: data?.usdToIqdRate,
            usd: 1,
        },
    ];

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">
                    Gold Rate in Erbil, Iraq
                </h1>

                <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-sm text-gray-600">
                        Updated live from official sources
                    </p>

                    <div className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-sm text-gray-700 shadow-sm">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        <span className="font-medium">
                            {now.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="tabular-nums">{now.toLocaleTimeString("en-US")}</span>
                    </div>
                </div>
            </div>

            {/* States */}
            {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                </div>
            )}

            {!data ? (
                <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <div className="animate-pulse space-y-3">
                        <div className="h-4 w-40 rounded bg-gray-200" />
                        <div className="h-10 rounded bg-gray-200" />
                        <div className="h-10 rounded bg-gray-200" />
                        <div className="h-10 rounded bg-gray-200" />
                    </div>
                </div>
            ) : data.liveGoldEnabled === false ? (
                <div className="rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-yellow-900">
                    {data.message || "Live gold is disabled."}
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
                    {/* Card header */}
                    <div className="flex items-center justify-between gap-3 border-b bg-gray-50 px-4 sm:px-6 py-4">
                        <div>
                            <p className="text-sm font-semibold text-gray-900">Live Rates</p>
                            <p className="text-xs text-gray-600">
                                Prices displayed per 5 grams (مثقال) in IQD and USD
                            </p>

                        </div>


                    </div>

                    {/* Table (desktop) */}
                    <div className="hidden sm:block">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs uppercase tracking-wider text-gray-500">
                                    <th className="text-left px-6 py-3">Item</th>
                                    <th className="text-right px-6 py-3">IQD</th>
                                    <th className="text-right px-6 py-3">USD</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y">
                                {rows.map((r, idx) => (
                                    <tr
                                        key={r.label}
                                        className={[
                                            idx % 2 === 0 ? "bg-white" : "bg-gray-50/40",
                                            r.highlight ? "bg-blue-50/40" : "",
                                            "hover:bg-gray-50 transition-colors",
                                        ].join(" ")}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{r.label}</div>
                                            <div className="text-xs text-gray-500">{r.sub}</div>
                                        </td>

                                        <td className="px-6 py-4 text-right">
                                            <span className="font-semibold tabular-nums text-gray-900">
                                                <span
                                                    className={`inline-flex items-center gap-1 font-semibold tabular-nums transition-all duration-500 ${r.label === "21K Gold" && directionMap.k21 === "up"
                                                        ? "text-green-600 scale-110"
                                                        : r.label === "21K Gold" && directionMap.k21 === "down"
                                                            ? "text-red-600 scale-110"
                                                            : r.label === "22K Gold" && directionMap.k22 === "up"
                                                                ? "text-green-600 scale-110"
                                                                : r.label === "22K Gold" && directionMap.k22 === "down"
                                                                    ? "text-red-600 scale-110"
                                                                    : r.label === "18K Gold" && directionMap.k18 === "up"
                                                                        ? "text-green-600 scale-110"
                                                                        : r.label === "18K Gold" && directionMap.k18 === "down"
                                                                            ? "text-red-600 scale-110"
                                                                            : "text-gray-900"
                                                        }`}
                                                >
                                                    {formatIQD(r.iqd)}

                                                    {r.label === "21K Gold" && directionMap.k21 === "up" && "▲"}
                                                    {r.label === "21K Gold" && directionMap.k21 === "down" && "▼"}

                                                    {r.label === "22K Gold" && directionMap.k22 === "up" && "▲"}
                                                    {r.label === "22K Gold" && directionMap.k22 === "down" && "▼"}

                                                    {r.label === "18K Gold" && directionMap.k18 === "up" && "▲"}
                                                    {r.label === "18K Gold" && directionMap.k18 === "down" && "▼"}
                                                </span>                                            </span>
                                            <span className="ml-2 text-xs text-gray-500">IQD</span>
                                        </td>

                                        <td className="px-6 py-4 text-right">
                                            <span className="font-semibold tabular-nums text-gray-900">
                                                {r.usd != null ? formatUSD(r.usd) : "-"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y">
                        {rows.map((r) => (
                            <div key={r.label} className="px-4 py-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="font-medium text-gray-900">{r.label}</p>
                                        <p className="text-xs text-gray-500">{r.sub}</p>
                                    </div>
                                    {r.label === "21K Gold" && (
                                        <span className="rounded-full bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 border border-blue-100">
                                            Most used
                                        </span>
                                    )}
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border bg-gray-50 px-3 py-2">
                                        <p className="text-[11px] uppercase tracking-wider text-gray-500">
                                            IQD
                                        </p>
                                        <p className="mt-1 font-semibold tabular-nums text-gray-900">
                                            <span
                                                className={`inline-flex items-center gap-1 font-semibold tabular-nums transition-all duration-500 ${r.label === "21K Gold" && directionMap.k21 === "up"
                                                    ? "text-green-600 scale-110"
                                                    : r.label === "21K Gold" && directionMap.k21 === "down"
                                                        ? "text-red-600 scale-110"
                                                        : r.label === "22K Gold" && directionMap.k22 === "up"
                                                            ? "text-green-600 scale-110"
                                                            : r.label === "22K Gold" && directionMap.k22 === "down"
                                                                ? "text-red-600 scale-110"
                                                                : r.label === "18K Gold" && directionMap.k18 === "up"
                                                                    ? "text-green-600 scale-110"
                                                                    : r.label === "18K Gold" && directionMap.k18 === "down"
                                                                        ? "text-red-600 scale-110"
                                                                        : "text-gray-900"
                                                    }`}
                                            >
                                                {formatIQD(r.iqd)}

                                                {r.label === "21K Gold" && directionMap.k21 === "up" && "▲"}
                                                {r.label === "21K Gold" && directionMap.k21 === "down" && "▼"}

                                                {r.label === "22K Gold" && directionMap.k22 === "up" && "▲"}
                                                {r.label === "22K Gold" && directionMap.k22 === "down" && "▼"}

                                                {r.label === "18K Gold" && directionMap.k18 === "up" && "▲"}
                                                {r.label === "18K Gold" && directionMap.k18 === "down" && "▼"}
                                            </span>                                        </p>
                                    </div>

                                    <div className="rounded-xl border bg-gray-50 px-3 py-2">
                                        <p className="text-[11px] uppercase tracking-wider text-gray-500">
                                            USD
                                        </p>
                                        <p className="mt-1 font-semibold tabular-nums text-gray-900">
                                            {r.usd != null ? formatUSD(r.usd) : "-"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer note */}
                    <div className="border-t bg-gray-50 px-4 sm:px-6 py-3">
                        <p className="text-xs text-gray-600">
                            Note: Rates may vary slightly between shops due to workmanship and market spread.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
